import { growthChartColors } from "../../lib/chart-colors";
import { growthAxes, metricTooltip, timeSeriesInteraction } from "../../lib/growth-chart";
import { istRangeStart, istDateKey, istISOString, formatCalendarDate } from "../../lib/admin-time";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext } from "react";
import { Line } from "react-chartjs-2";
import { APIContext } from "../../api/api";
import "chartjs-adapter-date-fns";
import {
  Chart as ChartJS,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  Legend,
  Tooltip,
} from "chart.js";
import HelmetLayout from "../../components/HelmetLayout";
import { PulseLoader } from "react-spinners";
import { Link } from "react-router-dom";
import {
  Users,
  Globe,
  TrendingUp,
  Palette,
  FileBox,
  Layers,
  ArrowUpRight,
} from "lucide-react";

ChartJS.register(
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
  TimeScale,
  Legend,
  Tooltip
);

const StatCard = ({ title, value, subtext, icon: Icon, color }) => (
  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-slate-500 text-sm font-medium mb-1">{title}</p>
        <h3 className="text-2xl font-bold text-slate-900">{value}</h3>
      </div>
      <div className={`p-3 rounded-xl ${color.bg}`}>
        <Icon size={22} className={color.text} />
      </div>
    </div>
    {subtext && (
      <div className="mt-3 flex items-center gap-2">
        <span className="text-slate-500 text-xs font-medium bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
          {subtext}
        </span>
      </div>
    )}
  </div>
);

export default function Dashboard() {
  const [loading, setLoading] = useState(true);
  const { getAnalyticsData } = useContext(APIContext);
  const [dashboardDataAnalytics, setDashboardDataAnalytics] = useState([]);
  const [dateOffset, setDateOffset] = useState(30);

  const getStartDate = istRangeStart;

  const dateString = getStartDate(dateOffset);

  useEffect(() => {
    setLoading(true);
    getAnalyticsData(dateString)
      .then((data) => {
        setDashboardDataAnalytics(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error(error.message);
        setLoading(false);
      });
  }, [dateOffset]);

  const latestData = dashboardDataAnalytics[dashboardDataAnalytics.length - 1] || {};
  const totalUsers = latestData.user_count || 0;
  const activeWebsites = latestData.active_projects || 0;
  const totalWebsites = latestData.lifetime_projects || 0;
  const totalNewUsers = dashboardDataAnalytics
    .map((item) => item.new_users_count || 0)
    .reduce((a, b) => parseInt(a, 10) + parseInt(b, 10), 0);

  const chartLabels = dashboardDataAnalytics.map((item) => formatCalendarDate(item.sk));
  const userCounts = dashboardDataAnalytics.map((item) => item.user_count);
  const newUsersCounts = dashboardDataAnalytics.map((item) => item.new_users_count);
  const activeProjectsCounts = dashboardDataAnalytics.map((item) => item.active_projects);
  const lifetimeProjects = dashboardDataAnalytics.map((item) => item.lifetime_projects);

  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: "Total Users",
        data: userCounts,
        fill: false,
        spanGaps: true,
        backgroundColor: growthChartColors.totalUsers,
        borderColor: growthChartColors.totalUsers,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
      },
      {
        label: "Active Websites",
        data: activeProjectsCounts,
        fill: false,
        spanGaps: true,
        backgroundColor: growthChartColors.activeWebsites,
        borderColor: growthChartColors.activeWebsites,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
      },
      {
        label: "Total Websites",
        data: lifetimeProjects,
        fill: false,
        spanGaps: true,
        backgroundColor: growthChartColors.totalWebsites,
        borderColor: growthChartColors.totalWebsites,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
      },
      {
        label: "New Signups",
        yAxisID: "daily",
        data: newUsersCounts,
        fill: false,
        spanGaps: true,
        backgroundColor: growthChartColors.newSignups,
        borderColor: growthChartColors.newSignups,
        borderWidth: 2,
        tension: 0.3,
        pointRadius: 0,
      },
    ],
  };

  const chartOptions = {
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: "circle",
          font: { family: "sans-serif", size: 12 },
        },
      },
      tooltip: metricTooltip(),
    },
    interaction: timeSeriesInteraction,
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        type: "category",
        parsing: false,
        time: { unit: "day" },
        grid: { display: false },
        ticks: { font: { size: 11 }, color: "#64748b" },
      },
      ...growthAxes("Total users / websites", "New signups / day", {
        dailyColor: growthChartColors.newSignups,
      }),
    },
  };

  return (
    <>
      <HelmetLayout title="Dashboard" />
      <div className="space-y-8 pb-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Dashboard</h1>
            <p className="text-slate-500 text-sm mt-0.5">
              Platform analytics, hosting metrics, and growth overview.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link target="_blank" rel="noopener noreferrer"
              to="/users"
              className="bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-sm"
            >
              View Users
            </Link>
            <Link target="_blank" rel="noopener noreferrer"
              to="/themes"
              className="bg-[#623ce7] hover:bg-[#5030bc] text-white px-4 py-2.5 rounded-lg text-sm font-medium transition-colors shadow-lg shadow-[#623ce7]/20"
            >
              Browse Themes
            </Link>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard
            title="Total Users"
            value={loading ? "—" : totalUsers.toLocaleString()}
            subtext="Registered accounts"
            icon={Users}
            color={{ bg: "bg-[#623ce7]/10", text: "text-[#623ce7]" }}
          />
          <StatCard
            title="Active Websites"
            value={loading ? "—" : activeWebsites.toLocaleString()}
            subtext="Published & live"
            icon={Globe}
            color={{ bg: "bg-emerald-50", text: "text-emerald-600" }}
          />
          <StatCard
            title="Total Websites"
            value={loading ? "—" : totalWebsites.toLocaleString()}
            subtext="Lifetime created"
            icon={Layers}
            color={{ bg: "bg-blue-50", text: "text-blue-600" }}
          />
          <StatCard
            title="New Signups"
            value={loading ? "—" : totalNewUsers.toLocaleString()}
            subtext={`Last ${dateOffset} days`}
            icon={TrendingUp}
            color={{ bg: "bg-purple-50", text: "text-purple-600" }}
          />
        </div>

        {/* Analytics Chart Card */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h3 className="text-lg font-bold text-slate-900">Growth & Activity</h3>
              <p className="text-xs text-slate-400 mt-0.5">Historical user signups and website activity</p>
            </div>
            <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
              {[7, 15, 30, 60].map((days) => (
                <button
                  key={days}
                  onClick={() => setDateOffset(days)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    dateOffset === days
                      ? "bg-white text-slate-900 shadow-sm"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {days}d
                </button>
              ))}
            </div>
          </div>

          <div className="h-[360px] w-full">
            {loading ? (
              <div className="h-full flex flex-col justify-center items-center">
                <PulseLoader color="#623ce7" size={10} />
                <p className="text-slate-400 text-xs mt-2">Loading analytics...</p>
              </div>
            ) : (
              <Line data={chartData} options={chartOptions} />
            )}
          </div>
        </div>

        {/* Quick Module Navigation */}
        <div>
          <h3 className="text-lg font-bold text-slate-900 mb-4">Quick Shortcuts</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Link target="_blank" rel="noopener noreferrer"
              to="/themes"
              className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-[#623ce7]/40 hover:shadow-md transition-all group flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-[#623ce7]/10 text-[#623ce7] rounded-lg">
                  <Palette size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 group-hover:text-[#623ce7] transition-colors">
                    Themes Library
                  </h4>
                  <p className="text-xs text-slate-400">Sync and edit site themes</p>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-slate-400 group-hover:text-[#623ce7] transition-colors" />
            </Link>

            <Link target="_blank" rel="noopener noreferrer"
              to="/templates"
              className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-[#623ce7]/40 hover:shadow-md transition-all group flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-50 text-blue-600 rounded-lg">
                  <FileBox size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">
                    Templates Library
                  </h4>
                  <p className="text-xs text-slate-400">Manage site templates</p>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-slate-400 group-hover:text-blue-600 transition-colors" />
            </Link>

            <Link target="_blank" rel="noopener noreferrer"
              to="/sections"
              className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm hover:border-[#623ce7]/40 hover:shadow-md transition-all group flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-emerald-50 text-emerald-600 rounded-lg">
                  <Layers size={20} />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 group-hover:text-emerald-600 transition-colors">
                    Sections Library
                  </h4>
                  <p className="text-xs text-slate-400">Manage UI component patterns</p>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-slate-400 group-hover:text-emerald-600 transition-colors" />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
